import LocalStorageService, {UserDetails, UserDetailsStore} from "./localStorageService";
import RedirectionService from "./redirectionService";
import RestService, {ErrorDetails, ResponseStatus, RestCall} from "./restService";
import EnvironmentService from "./environmentService";
import {LoginResponseDto, RefreshTokenResponseDto} from "../model/functionModels";

// Track if a token refresh is in progress to prevent concurrent refresh attempts
let refreshInProgress: Promise<UserDetailsStore | null> | null = null;
let refreshError: string | null = null;
let refreshInitiatedBy: {
    onSuccess: (userDetails: UserDetailsStore) => Promise<any>,
    onFailure: (error: string) => Promise<any>,
    onRedirection: (newUrl: string) => Promise<any>
} | null = null;

async function verifyLoggedIn(onSuccess: (userDetails : UserDetailsStore) => Promise<any>,
    onFailure: (error: string) => Promise<any>,
    onRedirection: (newUrl: string) => Promise<any>){
    const storeDetails: UserDetailsStore | undefined = LocalStorageService.getUserDetails();
    if (!storeDetails || storeDetails!.refreshTokenExpiry < Date.now() / 1000) {
        // not logged in so we need to redirect you to the login page.
        // your refresh token is expired so we have to redirect you to login screen
        console.log("User Token Expired! Exiting");
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/dashboard';
        await onRedirection(RedirectionService.redirectToLogin(currentPath))
    } else if (storeDetails!.accessTokenExpiry < Date.now() / 1000) {
        // your access token is expired. We will try to fetch a new one using the refresh token
        console.log("User Token Expired! Refreshing");
        
        // If a refresh is already in progress, wait for it instead of starting a new one
        if (refreshInProgress) {
            const refreshedDetails = await refreshInProgress;
            if (refreshedDetails) {
                await onSuccess(refreshedDetails);
            } else {
                // Refresh failed - only the initiator will show error (handled in attemptTokenRefreshing)
                // Other callers just redirect to login silently to prevent duplicate error messages
                const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/dashboard';
                await onRedirection(RedirectionService.redirectToLogin(currentPath));
            }
            return; // Exit early to prevent starting a new refresh
        }
        
        // No refresh in progress - this caller will initiate it
        // Store the callbacks for the caller that initiates the refresh BEFORE starting refresh
        // This ensures only one caller is marked as initiator
        refreshInitiatedBy = {onSuccess, onFailure, onRedirection};
        
        // Start the refresh - it will set refreshInProgress internally
        const refreshedDetails = await attemptTokenRefreshing(storeDetails);
        
        // Handle the result for this initiator
        if (refreshedDetails) {
            await onSuccess(refreshedDetails);
        } else {
            // Only show error for the initiator
            await onFailure(refreshError || "Failed refreshing with refresh token!");
        }
    } else {
        // you are logged in,
        await onSuccess(storeDetails);
    }
}

async function attemptTokenRefreshing(userDetails: UserDetailsStore): Promise<UserDetailsStore | null> {
    // Store userDetails in a const
    const userDetailsForRefresh = userDetails;
    refreshError = null;

    // Create a promise for the refresh attempt
    const refreshPromise = new Promise<UserDetailsStore | null>((resolve) => {
        RestService.doUnauthenticatedCall({
                url: EnvironmentService.getFunctionUrl() + "/auth/tokens/refresh",
                method: "post",
                body: {
                    refreshToken: userDetailsForRefresh.refreshToken
                },
            } as RestCall,
            async (response : ResponseStatus) => {
                const refreshResponse : RefreshTokenResponseDto = response.body;
                LocalStorageService.updateUserDetails(
                    userDetailsForRefresh.refreshToken,
                    refreshResponse.token,
                    refreshResponse.user as UserDetails);
                const updatedDetails = LocalStorageService.getUserDetails()!;
                refreshInProgress = null;
                refreshError = null;
                resolve(updatedDetails);
            },
            async (restCall : RestCall, error : ErrorDetails) => {
                console.log(`Failed refreshing with refresh token!`, error);
                const errorMessage = `Failed refreshing with refresh token!`;
                refreshError = errorMessage;
                refreshInProgress = null;
                resolve(null);
            });
    });

    // Set the refresh promise IMMEDIATELY so other concurrent calls can see it
    refreshInProgress = refreshPromise;
    
    // Wait for the refresh to complete and return the result
    return await refreshPromise;
}

async function attemptLogin(username: string, password: string,
                            onSuccess: () => Promise<any>,
                            onFailure: (errorDetails: string) => Promise<any>) {
    await RestService.doUnauthenticatedCall({
            url: EnvironmentService.getFunctionUrl() + "/auth/login",
            method: "post",
            body: {
                username: username,
                password: password
            },
        } as RestCall,
        async (response : ResponseStatus) => {
            const refreshResponse : LoginResponseDto = response.body;
            LocalStorageService.updateUserDetails(
                refreshResponse.refreshToken,
                refreshResponse.token,
                refreshResponse.user as UserDetails);
            await onSuccess();
        },
        async (restCall : RestCall, error : ErrorDetails) => {
            console.log(`Failed logging in with refresh token!`, error);
            await onFailure(error.error);
        })
}



async function forgotPassword(email: string,
                              onSuccess: () => Promise<any>,
                              onFailure: (errorDetails: string) => Promise<any>) {
    await RestService.doUnauthenticatedCall({
            url: EnvironmentService.getFunctionUrl() + "/auth/password/forgot",
            method: "post",
            body: {
                email: email
            },
        } as RestCall,
        async (response : ResponseStatus) => {
            await onSuccess();
        },
        async (restCall : RestCall, error : ErrorDetails) => {
            console.log(`Failed sending password reset email!`, error);
            await onFailure(error.error);
        })
}

async function resetPassword(token: string, newPassword: string,
                            onSuccess: () => Promise<any>,
                            onFailure: (errorDetails: string) => Promise<any>) {
    await RestService.doUnauthenticatedCall({
            url: EnvironmentService.getFunctionUrl() + "/auth/password/reset",
            method: "post",
            body: {
                token: token,
                newPassword: newPassword
            },
        } as RestCall,
        async (response : ResponseStatus) => {
            await onSuccess();
        },
        async (restCall : RestCall, error : ErrorDetails) => {
            console.log(`Failed resetting password!`, error);
            await onFailure(error.error);
        })
}

async function registerFromInvitation(token: string,
                                      firstName: string,
                                      lastName: string,
                                      username: string,
                                      password: string,
                                      onSuccess: () => Promise<any>,
                                      onFailure: (errorDetails: string) => Promise<any>) {
    await RestService.doUnauthenticatedCall({
            url: EnvironmentService.getFunctionUrl() + "/auth/register",
            method: "post",
            body: {
                token: token,
                firstName: firstName,
                lastName: lastName,
                username: username,
                password: password,
            },
        } as RestCall,
        async (response : ResponseStatus) => {
            const registerResponse : LoginResponseDto = response.body;
            LocalStorageService.updateUserDetails(
                registerResponse.refreshToken,
                registerResponse.token,
                registerResponse.user as UserDetails);
            await onSuccess();
        },
        async (_restCall : RestCall, error : ErrorDetails) => {
            console.log(`Failed registering from invitation!`, error);
            await onFailure(error.error);
        });
}

const AuthenticationService = {
    verifyLoggedIn,
    attemptLogin,
    forgotPassword,
    resetPassword,
    registerFromInvitation,
}

export default AuthenticationService;